import { inject, Pipe, PipeTransform } from '@angular/core';
import {
    DomSanitizer,
    SafeHtml,
    SafeStyle,
    SafeScript,
    SafeUrl,
    SafeResourceUrl,
} from '@angular/platform-browser';

/**
 * SafePipe marks strings as trusted to bypass Angular sanitization.
 *
 * It uses `DomSanitizer` and supports: 'html', 'style', 'script', 'url',
 * and 'resourceUrl'. Use carefully and only with trusted content.
 *
 * Template usage:
 *```html
 *   @if (htmlString) {
 *       <div [innerHTML]="htmlString | safe: 'html'"></div>
 *   }
 * ```
 */
@Pipe({
    name: 'safe',
    standalone: true,
})
export class SafePipe implements PipeTransform {
    protected sanitizer = inject(DomSanitizer);

    /**
     * Mark string as trusted to bypass Angular sanitization.
     *
     * @param value Input string.
     * @param type One of: 'html' | 'style' | 'script' | 'url' | 'resourceUrl'.
     * @returns Safe value matching selected type.
     */
    transform(
        value: string,
        type: string,
    ): SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl {
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                return this.sanitizer.bypassSecurityTrustHtml(value);
        }
    }
}
